import { useQueries } from '@tanstack/react-query';
import { fetchPracticeDetail } from '../services/practiceDetail';
import { PRACTICE_DETAIL_KEY } from './queryKeys';
import type { UserPractice } from '../services/users';

/**
 * Resolves display names for a set of practice ids that only came back as
 * raw ids - the Users list deep-link case's single-user GET returns
 * `practices` as string[] of ids (see services/users.ts's UserRecord),
 * unlike the paginated list rows which already carry `{ id, name }`. Shares
 * PRACTICE_DETAIL_KEY's cache with usePracticeDetail, so a practice already
 * viewed on its own detail page resolves without another round trip.
 *
 * A practice that fails to resolve (deleted, or a transient error) reports
 * `name: null` - callers fall back to the muted raw id, same convention as
 * PracticeChip / PracticeMultiSelect.
 */
export function usePracticeNames(ids: string[]): { practices: UserPractice[]; isLoading: boolean } {
    const results = useQueries({
        queries: ids.map((id) => ({
            queryKey: PRACTICE_DETAIL_KEY(id),
            queryFn: () => fetchPracticeDetail(id),
            staleTime: 60_000,
            retry: false
        }))
    });

    const practices: UserPractice[] = ids.map((id, index) => ({
        id,
        name: results[index]?.data?.name ?? null
    }));

    const isLoading = results.some((result) => result.isLoading);

    return { practices, isLoading };
}
